import React, { useState, useEffect } from "react";
import {
  FlowConfigService,
  FlowConfigResponse
} from "../../../../modules/configuration-hub/data/services/flow-config.service";
import { ProcessingHubService } from "../../../../modules/processing-hub/data/services/processing-hub.service";
import { DocumentTypeInformation } from "../../../../modules/processing-hub/data/models/processing-hub.model";
import "./ViewFlowConfigurationModal.scss";

interface ViewFlowConfigurationModalProps {
  isOpen: boolean;
  onClose: () => void;
  configurationId: number | null;
}

const ViewFlowConfigurationModal: React.FC<ViewFlowConfigurationModalProps> = ({
  isOpen,
  onClose,
  configurationId
}) => {
  const [configData, setConfigData] = useState<FlowConfigResponse | null>(null);
  const [documentTypeInfo, setDocumentTypeInfo] = useState<
    DocumentTypeInformation[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch configuration details when modal opens
  useEffect(() => {
    if (isOpen && configurationId) {
      fetchConfigurationDetails();
    }
  }, [isOpen, configurationId]);

  const fetchConfigurationDetails = async () => {
    if (!configurationId) return;

    setIsLoading(true);
    setError(null);
    // Clear previous data to prevent showing stale information
    setConfigData(null);
    setDocumentTypeInfo([]);

    try {
      // Fetch flow configuration details
      const flowConfigResult = await FlowConfigService.getFlowConfigurationById(
        configurationId
      );

      if (!flowConfigResult.hasErrors && flowConfigResult.value) {
        setConfigData(flowConfigResult.value);

        // Try to fetch document type information for additional details
        // We'll try common document types including "Others" to find the matching config
        const documentTypes = ["Engineering", "Contract", "Invoice", "Others"];
        let foundMatchingConfig = false;

        for (const docType of documentTypes) {
          try {
            const docTypeResult =
              await ProcessingHubService.getDocumentTypeInformation(docType);

            if (docTypeResult.data && !docTypeResult.error) {
              // Find matching configuration by flow_id, id, or config_id
              const matchingConfig = docTypeResult.data.find(
                (item: DocumentTypeInformation) => {
                  const flowConfigId = flowConfigResult.value?.config_id;
                  const matches =
                    item.flow_id === configurationId ||
                    (item as any).id === configurationId ||
                    (item as any).config_id === configurationId ||
                    // Also try matching with the config_id from the flow config data
                    (flowConfigId &&
                      (item.flow_id === flowConfigId ||
                        (item as any).id === flowConfigId ||
                        (item as any).config_id === flowConfigId));

                  return matches;
                }
              );

              if (matchingConfig) {
                setDocumentTypeInfo([matchingConfig]);
                foundMatchingConfig = true;
                break; // Found the matching config, stop searching
              }
            }
          } catch (docTypeError) {
            console.error(
              `Error fetching ${docType} document type:`,
              docTypeError
            );
            // Continue to next document type if this one fails
          }
        }

        // If we couldn't find the config in document type information,
        // try to get extraction config name using alternative approaches
        if (
          !foundMatchingConfig &&
          flowConfigResult.value &&
          flowConfigResult.value.config_id
        ) {
          try {
            const configId = flowConfigResult.value.config_id;

            // Try to search through all document types to find the config by config_id
            const allDocTypes = [
              "Engineering",
              "Contract",
              "Invoice",
              "Others"
            ];
            let foundConfig = null;

            // Import DocumentTypeConfigService dynamically to avoid circular dependencies
            const { DocumentTypeConfigService } = await import(
              "../../../../modules/configuration-hub/data/services/document-type-config.service"
            );

            for (const docType of allDocTypes) {
              try {
                const configResult =
                  await DocumentTypeConfigService.getDocumentTypeConfigurations(
                    docType
                  );
                if (!configResult.hasErrors && configResult.value) {
                  // Find configuration by config_id (using id field)
                  foundConfig = configResult.value.find((config) => {
                    return config.id === configId;
                  });

                  if (foundConfig) {
                    setDocumentTypeInfo([
                      {
                        flow_id: configurationId,
                        config_name: foundConfig.config_name,
                        config_id: configId,
                        document_type: foundConfig.document_type || docType
                      } as DocumentTypeInformation
                    ]);
                    break;
                  }
                }
              } catch (searchError) {
                console.error(
                  `Error searching for config in ${docType}:`,
                  searchError
                );
              }
            }

            if (!foundConfig) {
              // Check if there's a config_name directly in the flow config data
              const directConfigName = flowConfigResult.value?.config_name;

              if (directConfigName) {
                setDocumentTypeInfo([
                  {
                    flow_id: configurationId,
                    config_name: directConfigName,
                    config_id: configId
                  } as DocumentTypeInformation
                ]);
              } else {
                // Create minimal document type info with config_id for display
                setDocumentTypeInfo([
                  {
                    flow_id: configurationId,
                    config_name: `Configuration ID: ${configId}`,
                    config_id: configId
                  } as DocumentTypeInformation
                ]);
              }
            }
          } catch (extractionError) {
            console.error("Error fetching extraction config:", extractionError);
          }
        }
      } else {
        setError(
          flowConfigResult.errors.join(", ") ||
            "Failed to load configuration details"
        );
      }
    } catch (err) {
      setError("Error loading configuration details");
      console.error("Error fetching flow configuration:", err);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="view-flow-modal-overlay">
      <div className="view-flow-modal-container">
        {/* Modal Header */}
        <div className="view-flow-modal-header">
          <h2>Flow Configuration Details</h2>
          <button className="close-button" onClick={onClose}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        {/* Modal Body */}
        <div className="view-flow-modal-body">
          {isLoading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading flow configuration details...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <div className="error-icon">⚠️</div>
              <p>{error}</p>
              <button
                className="retry-button"
                onClick={fetchConfigurationDetails}
              >
                Try Again
              </button>
            </div>
          ) : configData ? (
            <div className="flow-configuration-details">
              {/* Basic Information */}
              <div className="detail-section">
                <h3 className="section-title">Basic Information</h3>
                <div className="detail-grid">
                  {configData.flow_name && (
                    <div className="detail-item full-width">
                      <label>Flow name</label>
                      <div className="detail-value">{configData.flow_name}</div>
                    </div>
                  )}

                  {configData.flow_description && (
                    <div className="detail-item full-width">
                      <label>Flow description</label>
                      <div className="detail-value description">
                        {configData.flow_description}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Document Operations - Only show enabled operations */}
              {(() => {
                const enabledOperations = [];

                if (configData.ingestion) {
                  enabledOperations.push({
                    name: "Ingestion",
                    enabled: true,
                    details: configData.upload_method
                      ? `Upload Method: ${
                          configData.upload_method === "blob"
                            ? "Blob Storage"
                            : configData.upload_method === "local"
                            ? "Local Upload"
                            : configData.upload_method === "manual"
                            ? "Local"
                            : configData.upload_method
                        }`
                      : undefined
                  });
                }

                if (configData.extraction) {
                  const extractionConfigName =
                    documentTypeInfo.length > 0 &&
                    documentTypeInfo[0].config_name
                      ? documentTypeInfo[0].config_name
                      : configData.config_name;

                  enabledOperations.push({
                    name: "Extraction",
                    enabled: true,
                    details: extractionConfigName
                      ? `Configuration: ${extractionConfigName}`
                      : undefined
                  });
                }

                if (configData.tagging) {
                  enabledOperations.push({
                    name: "Tagging",
                    enabled: true
                  });
                }

                if (configData.similarity) {
                  enabledOperations.push({
                    name: "Similarity",
                    enabled: true
                  });
                }

                if (configData.export) {
                  enabledOperations.push({
                    name: "Export",
                    enabled: true
                  });
                }

                return enabledOperations.length > 0 ? (
                  <div className="detail-section">
                    <h3 className="section-title">Document Operations</h3>
                    <div className="operations-grid">
                      {enabledOperations.map((operation, index) => (
                        <div key={index} className="operation-item enabled">
                          <div className="operation-icon">✓</div>
                          <div className="operation-details">
                            <span className="operation-name">
                              {operation.name}
                            </span>
                            {operation.details && (
                              <span className="operation-details-text">
                                {operation.details}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null;
              })()}

              {/* Configuration Details */}
              {/* <div className="detail-section">
                <h3 className="section-title">Configuration Details</h3>
                <div className="detail-grid">
                  {configData.upload_method && (
                    <div className="detail-item">
                      <label>Upload Method</label>
                      <div className="detail-value upload-method">
                        <span
                          className={`method-badge ${configData.upload_method.toLowerCase()}`}
                        >
                          {configData.upload_method === "blob"
                            ? "Blob Storage"
                            : configData.upload_method === "local"
                            ? "Local Upload"
                            : configData.upload_method === "manual"
                            ? "Local"
                            : configData.upload_method}
                        </span>
                      </div>
                    </div>
                  )} */}

              {/* Extraction Configuration - get from document type info */}
              {/* <div className="detail-item">
                    <label>Extraction Configuration</label>
                    <div className="detail-value extraction-config">
                      {(() => {
                        let configName = null;
                        if (
                          documentTypeInfo.length > 0 &&
                          documentTypeInfo[0].config_name
                        ) {
                          configName = documentTypeInfo[0].config_name;
                        }
                        else if (configData.config_name) {
                          configName = configData.config_name;
                        }
                        else if (configData.config_id) {
                          configName = `Config ID: ${configData.config_id}`;
                        }

                        return configName ? (
                          <span className="config-name" title={configName}>
                            {configName}
                          </span>
                        ) : (
                          <span className="config-name-empty">
                            Not specified
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              </div> */}
            </div>
          ) : null}
        </div>

        {/* Modal Footer */}
        <div className="view-flow-modal-footer">
          <button className="close-modal-button" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewFlowConfigurationModal;
